import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { useAuthStore } from '@/store';
import Layout from '@/components/Layout';
import ConfirmHost from '@/components/ConfirmHost';
import { ADMIN_ONLY, ADMIN_AND_CM, ALL_EXCEPT_VIEWER } from '@/config/navigation';

// ─── Lazy-loaded page components (issue #317 — route-level code-splitting) ──
// Each page is loaded on demand, reducing the initial JS bundle from ~3.6MB
// to just the main + vendor chunks. Route chunks are generated automatically
// by Vite's dynamic import.

// Lazy so three.js and the landing page ship in their own chunk — a signed-in
// user opening the app never downloads either.
const Landing = lazy(() => import('@/pages/Landing'));

const ChartOfAccounts = lazy(() => import('@/pages/ChartOfAccounts'));
const TrialBalances = lazy(() => import('@/pages/TrialBalances'));
const Consolidation = lazy(() => import('@/pages/Consolidation'));
const ConsolidationWaterfall = lazy(() => import('@/pages/ConsolidationWaterfall'));
const DataStatus = lazy(() => import('@/pages/DataStatus'));
const Reports = lazy(() => import('@/pages/Reports'));
const Settings = lazy(() => import('@/pages/Settings'));
const Rules = lazy(() => import('@/pages/Rules'));
const TranslationOverrideRules = lazy(() => import('@/pages/TranslationOverrideRules'));
const CurrencyTriangulation = lazy(() => import('@/pages/CurrencyTriangulation'));
const CalcManager = lazy(() => import('@/pages/CalcManager'));
const JournalEntries = lazy(() => import('@/pages/JournalEntries'));
const Journals = lazy(() => import('@/pages/Journals'));
const ManageOwnership = lazy(() => import('@/pages/ManageOwnership'));
const EntityOwnershipStructure = lazy(() => import('@/pages/EntityOwnershipStructure'));
const MenuControl = lazy(() => import('@/pages/MenuControl'));
const Jobs = lazy(() => import('@/pages/Jobs'));
const ValidIntersections = lazy(() => import('@/pages/ValidIntersections'));
const AuditTrail = lazy(() => import('@/pages/AuditTrail'));
const DimensionManager = lazy(() => import('@/pages/DimensionManager'));
const ReportBuilder = lazy(() => import('@/pages/ReportBuilder'));
const FootnoteManager = lazy(() => import('@/pages/FootnoteManager'));
const ValidationExceptions = lazy(() => import('@/pages/ValidationExceptions'));
const SoDManagement = lazy(() => import('@/pages/SoDManagement'));
const FormWorkbench = lazy(() => import('@/pages/FormWorkbench'));
const DataForms = lazy(() => import('@/pages/DataForms'));
const FormDataEntry = lazy(() => import('@/pages/FormDataEntry'));
const SmartView = lazy(() => import('@/pages/SmartView'));
const MovementSchedule = lazy(() => import('@/pages/MovementSchedule'));
const DataLoadConfig = lazy(() => import('@/pages/DataLoadConfig'));
const ConnectionManager = lazy(() => import('@/pages/ConnectionManager'));
const MappingRules = lazy(() => import('@/pages/MappingRules'));
const IntegrationDashboard = lazy(() => import('@/pages/IntegrationDashboard'));
const IntegrationProcessLog = lazy(() => import('@/pages/IntegrationProcessLog'));
const DataReconciliation = lazy(() => import('@/pages/DataReconciliation'));
const ImportFormatTemplates = lazy(() => import('@/pages/ImportFormatTemplates'));
const PeriodMappings = lazy(() => import('@/pages/PeriodMappings'));
const DataProfiles = lazy(() => import('@/pages/DataProfiles'));
const CheckValidationReport = lazy(() => import('@/pages/CheckValidationReport'));
const LoadValidateConfirm = lazy(() => import('@/pages/LoadValidateConfirm'));
const DataValidationWorkbench = lazy(() => import('@/pages/DataValidationWorkbench'));
const SetupApplications = lazy(() => import('@/pages/SetupApplications'));
const CustomScripts = lazy(() => import('@/pages/CustomScripts'));
const QueryDefinitions = lazy(() => import('@/pages/QueryDefinitions'));
const DataExchange = lazy(() => import('@/pages/DataExchange'));
const ApplicationCubes = lazy(() => import('@/pages/ApplicationCubes'));
const AliasTables = lazy(() => import('@/pages/AliasTables'));
const AliasTableEntries = lazy(() => import('@/pages/AliasTableEntries'));
const Variables = lazy(() => import('@/pages/Variables'));
const SdmRates = lazy(() => import('@/pages/SdmRates'));
const ClearData = lazy(() => import('@/pages/ClearData'));
const CopyData = lazy(() => import('@/pages/CopyData'));
const ReportingStudio = lazy(() => import('@/pages/reporting/ReportingStudio'));
const ReportPlaceholder = lazy(() => import('@/pages/reporting/ReportPlaceholder'));
const ReportDesigner = lazy(() => import('@/pages/reporting/ReportDesigner'));
const BookDesigner = lazy(() => import('@/pages/reporting/BookDesigner'));
const ReportViewer = lazy(() => import('@/pages/reporting/ReportViewer'));

// ─── Route-level loading skeleton ────────────────────────────────────────────

function PageSkeleton() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-700 rounded-full animate-spin" />
        <p className="text-sm text-gray-500">Loading…</p>
      </div>
    </div>
  );
}

// ─── Protected Route ────────────────────────────────────────────────────────

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-700 rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // The landing page IS the sign-in page — there is no separate form screen.
    return <Navigate to="/welcome" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

// ─── Role-based Route Guard ─────────────────────────────────────────────────

function RoleRoute({ roles, children }: { roles?: string[]; children: ReactNode }) {
  const { user } = useAuthStore();
  if (roles && roles.length > 0 && user && !roles.includes(user.role)) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-700 mb-2">Access Denied</h2>
          <p className="text-gray-500">You don&apos;t have permission to view this page.</p>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}

// ─── App ────────────────────────────────────────────────────────────────────

export default function App() {
  const { loadUser, isAuthenticated, token } = useAuthStore();

  useEffect(() => {
    if (token) {
      loadUser();
    }
  }, [token, loadUser]);

  return (
    <>
    <Routes>
      {/* Public routes */}
      <Route
        path="/welcome"
        element={
          isAuthenticated ? <Navigate to="/" replace /> : (
            <Suspense fallback={<PageSkeleton />}>
              <Landing />
            </Suspense>
          )
        }
      />
      {/*
        There is no separate sign-in page any more — the landing page carries
        the form, so signing in and reading about the product are one screen.
        The path stays as a redirect: it is bookmarked, it is what older builds
        and any external link point at, and 404-ing those would lock people out.
      */}
      <Route path="/login" element={<Navigate to="/welcome" replace />} />

      {/*
        Smart View / Ad hoc grid — deliberately OUTSIDE the app shell so it fills
        the window like a spreadsheet application: no nav rail, no page chrome.
        Launched from Data entry → Actions (?seed=&mode=).
      */}
      <Route
        path="/smart-view"
        element={
          <ProtectedRoute>
            <Suspense fallback={<PageSkeleton />}>
              <SmartView />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* Protected routes with Layout */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dimensions" replace />} />
        <Route path="manage-ownership" element={<Suspense fallback={<PageSkeleton />}><ManageOwnership /></Suspense>} />
        <Route path="entity-ownership-structure" element={<Suspense fallback={<PageSkeleton />}><EntityOwnershipStructure /></Suspense>} />
        <Route path="chart-of-accounts" element={<Suspense fallback={<PageSkeleton />}><ChartOfAccounts /></Suspense>} />
        <Route path="trial-balances" element={<Suspense fallback={<PageSkeleton />}><TrialBalances /></Suspense>} />
        <Route path="movement-schedule" element={<Suspense fallback={<PageSkeleton />}><MovementSchedule /></Suspense>} />
        <Route path="data-load-config" element={<RoleRoute roles={ALL_EXCEPT_VIEWER}><Suspense fallback={<PageSkeleton />}><DataLoadConfig /></Suspense></RoleRoute>} />
        <Route path="integration/connections" element={<Suspense fallback={<PageSkeleton />}><ConnectionManager /></Suspense>} />
        <Route path="integration/mapping-rules" element={<Suspense fallback={<PageSkeleton />}><MappingRules /></Suspense>} />
        <Route path="integration" element={<Suspense fallback={<PageSkeleton />}><IntegrationDashboard /></Suspense>} />
        <Route path="integration/process-log" element={<Suspense fallback={<PageSkeleton />}><IntegrationProcessLog /></Suspense>} />
        <Route path="integration/reconciliation" element={<Suspense fallback={<PageSkeleton />}><DataReconciliation /></Suspense>} />
        <Route path="integration/import-templates" element={<Suspense fallback={<PageSkeleton />}><ImportFormatTemplates /></Suspense>} />
        <Route path="integration/period-mappings" element={<Suspense fallback={<PageSkeleton />}><PeriodMappings /></Suspense>} />
        <Route path="integration/data-profiles" element={<Suspense fallback={<PageSkeleton />}><DataProfiles /></Suspense>} />
        <Route path="integration/check-validation" element={<Suspense fallback={<PageSkeleton />}><CheckValidationReport /></Suspense>} />
        <Route path="integration/load-validate-confirm" element={<Suspense fallback={<PageSkeleton />}><LoadValidateConfirm /></Suspense>} />
        <Route path="integration/workbench" element={<Suspense fallback={<PageSkeleton />}><DataValidationWorkbench /></Suspense>} />
        <Route path="integration/setup-applications" element={<Suspense fallback={<PageSkeleton />}><SetupApplications /></Suspense>} />
        <Route path="setup/alias-tables" element={<RoleRoute roles={ADMIN_ONLY}><Suspense fallback={<PageSkeleton />}><AliasTables /></Suspense></RoleRoute>} />
        <Route path="setup/alias-tables/:id/entries" element={<RoleRoute roles={ADMIN_ONLY}><Suspense fallback={<PageSkeleton />}><AliasTableEntries /></Suspense></RoleRoute>} />
        <Route path="integration/scripts" element={<Suspense fallback={<PageSkeleton />}><CustomScripts /></Suspense>} />
        <Route path="integration/queries" element={<Suspense fallback={<PageSkeleton />}><QueryDefinitions /></Suspense>} />
        <Route path="data-exchange" element={<Suspense fallback={<PageSkeleton />}><DataExchange /></Suspense>} />
        <Route path="journal-entries" element={<RoleRoute roles={ALL_EXCEPT_VIEWER}><Suspense fallback={<PageSkeleton />}><JournalEntries /></Suspense></RoleRoute>} />
        <Route path="journals" element={<RoleRoute roles={ALL_EXCEPT_VIEWER}><Suspense fallback={<PageSkeleton />}><Journals /></Suspense></RoleRoute>} />
        <Route path="old-consolidation" element={<Suspense fallback={<PageSkeleton />}><Consolidation /></Suspense>} />
        <Route path="data-status" element={<RoleRoute roles={ALL_EXCEPT_VIEWER}><Suspense fallback={<PageSkeleton />}><DataStatus /></Suspense></RoleRoute>} />
        <Route path="consolidation-waterfall" element={<RoleRoute roles={ALL_EXCEPT_VIEWER}><Suspense fallback={<PageSkeleton />}><ConsolidationWaterfall /></Suspense></RoleRoute>} />
        <Route path="reports" element={<Suspense fallback={<PageSkeleton />}><Reports /></Suspense>} />
        <Route path="reporting" element={<Suspense fallback={<PageSkeleton />}><ReportingStudio /></Suspense>} />
        <Route path="reporting/designer/:reportId" element={<RoleRoute roles={ALL_EXCEPT_VIEWER}><Suspense fallback={<PageSkeleton />}><ReportDesigner /></Suspense></RoleRoute>} />
        <Route path="reporting/books/:bookId" element={<RoleRoute roles={ALL_EXCEPT_VIEWER}><Suspense fallback={<PageSkeleton />}><BookDesigner /></Suspense></RoleRoute>} />
        <Route path="reporting/view/:reportId" element={<RoleRoute roles={ALL_EXCEPT_VIEWER}><Suspense fallback={<PageSkeleton />}><ReportViewer /></Suspense></RoleRoute>} />
        <Route path="reporting/reports/:id" element={<Suspense fallback={<PageSkeleton />}><ReportPlaceholder /></Suspense>} />
        <Route path="audit" element={<Suspense fallback={<PageSkeleton />}><AuditTrail /></Suspense>} />
        <Route path="currency-triangulation" element={<Suspense fallback={<PageSkeleton />}><CurrencyTriangulation /></Suspense>} />
        <Route path="consolidation-rules" element={<RoleRoute roles={ADMIN_AND_CM}><Suspense fallback={<PageSkeleton />}><Rules /></Suspense></RoleRoute>} />
        <Route path="consolidation-rules/translation-overrides" element={<RoleRoute roles={ADMIN_AND_CM}><Suspense fallback={<PageSkeleton />}><TranslationOverrideRules /></Suspense></RoleRoute>} />
        <Route path="sdm-rates" element={<RoleRoute roles={ADMIN_AND_CM}><Suspense fallback={<PageSkeleton />}><SdmRates /></Suspense></RoleRoute>} />
        <Route path="calc-manager" element={<Suspense fallback={<PageSkeleton />}><CalcManager /></Suspense>} />
        <Route path="dimensions" element={<Suspense fallback={<PageSkeleton />}><DimensionManager /></Suspense>} />
        <Route path="valid-intersections" element={<RoleRoute roles={ADMIN_AND_CM}><Suspense fallback={<PageSkeleton />}><ValidIntersections /></Suspense></RoleRoute>} />
        <Route path="application-cubes" element={<RoleRoute roles={ADMIN_AND_CM}><Suspense fallback={<PageSkeleton />}><ApplicationCubes /></Suspense></RoleRoute>} />
        <Route path="forms/*" element={<Suspense fallback={<PageSkeleton />}><FormWorkbench /></Suspense>} />
        <Route path="data" element={<Suspense fallback={<PageSkeleton />}><DataForms /></Suspense>} />
        <Route path="data/:id" element={<Suspense fallback={<PageSkeleton />}><FormDataEntry /></Suspense>} />
        <Route path="oracle-forms" element={<Navigate to="/forms" replace />} />
        <Route path="report-builder" element={<Suspense fallback={<PageSkeleton />}><ReportBuilder /></Suspense>} />
        <Route path="footnotes" element={<Suspense fallback={<PageSkeleton />}><FootnoteManager /></Suspense>} />
        <Route path="validation-exceptions" element={<Suspense fallback={<PageSkeleton />}><ValidationExceptions /></Suspense>} />
        <Route path="sod" element={<Suspense fallback={<PageSkeleton />}><SoDManagement /></Suspense>} />
        <Route path="variables" element={<RoleRoute roles={ADMIN_AND_CM}><Suspense fallback={<PageSkeleton />}><Variables /></Suspense></RoleRoute>} />
        <Route path="jobs" element={<RoleRoute roles={ADMIN_AND_CM}><Suspense fallback={<PageSkeleton />}><Jobs /></Suspense></RoleRoute>} />
        {/* Application ▸ maintenance utilities (parent has no page of its own) */}
        <Route path="application" element={<Navigate to="/application/clear-data" replace />} />
        <Route path="application/clear-data" element={<RoleRoute roles={ADMIN_AND_CM}><Suspense fallback={<PageSkeleton />}><ClearData /></Suspense></RoleRoute>} />
        <Route path="application/copy-data" element={<RoleRoute roles={ADMIN_AND_CM}><Suspense fallback={<PageSkeleton />}><CopyData /></Suspense></RoleRoute>} />
        <Route path="menu-control" element={<RoleRoute roles={ADMIN_ONLY}><Suspense fallback={<PageSkeleton />}><MenuControl /></Suspense></RoleRoute>} />
        <Route path="settings" element={<RoleRoute roles={ADMIN_ONLY}><Suspense fallback={<PageSkeleton />}><Settings /></Suspense></RoleRoute>} />
      </Route>

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    <ConfirmHost />
    </>
  );
}
